import React, { useState } from 'react';
import { Flashcard, PaperData } from '../types/paper';
import { MathView } from './MathView';
import { exportToAnkiTSV, exportToMarkdown } from '../services/exportService';
import { 
  Layers, RotateCw, CheckCircle2, Flame, Award, Download, 
  Eye, Filter, Sparkles, Image as ImageIcon, ExternalLink, Zap
} from 'lucide-react';

interface FlashcardStudioProps {
  paper: PaperData;
}

export const FlashcardStudio: React.FC<FlashcardStudioProps> = ({ paper }) => {
  const [cards, setCards] = useState<Flashcard[]>(paper.flashcards);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isFlipped, setIsFlipped] = useState<boolean>(false);
  const [selectedTag, setSelectedCategoryTag] = useState<string>('all');
  const [zoomFigureUrl, setZoomFigureUrl] = useState<string | null>(null);

  // Extract unique tags
  const allTags = Array.from(new Set(paper.flashcards.flatMap(c => c.tags)));

  const filteredCards = selectedTag === 'all' 
    ? cards 
    : cards.filter(c => c.tags.includes(selectedTag));

  const currentCard = filteredCards[currentIndex] || filteredCards[0];

  const handleRating = (difficultyMultiplier: number) => {
    if (!currentCard) return;

    // Update SRS stage
    const updatedCards = cards.map(c => {
      if (c.id === currentCard.id) {
        return {
          ...c,
          srsStage: Math.min(3, (c.srsStage || 0) + 1)
        };
      }
      return c;
    });

    setCards(updatedCards);
    setIsFlipped(false);

    // Next card
    if (currentIndex < filteredCards.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setCurrentIndex(0);
    }
  };

  return (
    <div className="glass-panel rounded-2xl p-6 border border-slate-800 shadow-2xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-4 border-b border-slate-800">
        <div>
          <div className="flex items-center gap-2 text-brand-glow text-sm font-semibold uppercase tracking-wider">
            <Layers className="w-4 h-4" />
            Spaced Repetition Flashcard Studio
          </div>
          <h2 className="text-2xl font-bold text-white">
            High-Yield Paper Flashcards
          </h2>
        </div>

        {/* Action Buttons: Anki & Markdown Exports */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => exportToAnkiTSV(paper, cards)}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl text-xs font-semibold shadow-glow-blue transition-all"
          >
            <Download className="w-3.5 h-3.5" />
            Export to Anki (.tsv)
          </button>
          <button
            onClick={() => exportToMarkdown(paper)}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded-xl text-xs font-semibold transition-all"
          >
            <Download className="w-3.5 h-3.5" />
            Export Markdown
          </button>
        </div>
      </div>

      {/* Tags Filter & Progress Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2 overflow-x-auto pb-1 max-w-xl">
          <span className="text-xs font-semibold text-slate-400 flex items-center gap-1">
            <Filter className="w-3 h-3" /> Tag:
          </span>
          <button
            onClick={() => { setSelectedCategoryTag('all'); setCurrentIndex(0); }}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
              selectedTag === 'all'
                ? 'bg-brand-600 text-white shadow-glow-blue'
                : 'bg-dark-bg text-slate-400 hover:text-white border border-slate-800'
            }`}
          >
            All ({cards.length})
          </button>
          {allTags.map(tag => (
            <button
              key={tag}
              onClick={() => { setSelectedCategoryTag(tag); setCurrentIndex(0); }}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                selectedTag === tag
                  ? 'bg-brand-600 text-white shadow-glow-blue'
                  : 'bg-dark-bg text-slate-400 hover:text-white border border-slate-800'
              }`}
            >
              #{tag}
            </button>
          ))}
        </div>

        {/* Progress Counter */}
        <div className="text-xs font-semibold text-slate-400 bg-dark-bg/80 px-3 py-1.5 rounded-xl border border-slate-800">
          Card <span className="text-brand-400 font-mono font-bold">{currentIndex + 1}</span> of <span className="text-slate-200 font-mono">{filteredCards.length}</span>
        </div>
      </div>

      {/* Main Flashcard Container */}
      {currentCard ? (
        <div className="space-y-6">
          {/* 3D Flip Card */}
          <div className="perspective-1000 min-h-[380px] w-full">
            <div
              onClick={() => setIsFlipped(!isFlipped)}
              className={`relative w-full min-h-[380px] rounded-2xl cursor-pointer transition-transform duration-500 transform-style-3d ${
                isFlipped ? 'rotate-y-180' : ''
              }`}
            >
              {/* FRONT OF CARD */}
              <div className="absolute inset-0 w-full h-full glass-card p-8 rounded-2xl border border-slate-700/80 flex flex-col justify-between backface-hidden shadow-2xl">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-brand-900/60 text-brand-300 border border-brand-500/30 uppercase tracking-wider font-mono">
                      FRONT • QUESTION
                    </span>
                    <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase font-mono ${
                      currentCard.difficulty === 'extreme' ? 'bg-rose-950/80 text-rose-300 border border-rose-500/40' :
                      currentCard.difficulty === 'hard' ? 'bg-amber-950/80 text-amber-300 border border-amber-500/40' :
                      'bg-emerald-950/80 text-emerald-300 border border-emerald-500/40'
                    }`}>
                      {currentCard.difficulty || 'medium'}
                    </span>
                  </div>

                  {/* Question Prompt */}
                  <h3 className="text-xl md:text-2xl font-bold text-white leading-relaxed mb-4">
                    {currentCard.question}
                  </h3>

                  {/* Optional Figure Preview Thumbnail */}
                  {currentCard.figureUrl && (
                    <div className="mt-4 p-3 bg-dark-bg/80 rounded-xl border border-slate-800 flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-cyan-300 font-medium">
                        <ImageIcon className="w-4 h-4" /> Contains Original Paper Figure
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); setZoomFigureUrl(currentCard.figureUrl!); }}
                        className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 px-2.5 py-1 rounded-lg border border-slate-700 flex items-center gap-1"
                      >
                        <Eye className="w-3 h-3" /> Preview Asset
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-slate-800 text-xs text-slate-400">
                  <div className="flex flex-wrap gap-1.5">
                    {currentCard.tags.map(t => (
                      <span key={t} className="text-slate-400">#{t}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-1 text-brand-glow font-semibold animate-pulse">
                    <RotateCw className="w-3.5 h-3.5" /> Click anywhere to reveal answer
                  </div>
                </div>
              </div>

              {/* BACK OF CARD */}
              <div className="absolute inset-0 w-full h-full glass-card p-8 rounded-2xl border border-brand-500/40 flex flex-col justify-between backface-hidden rotate-y-180 shadow-2xl overflow-y-auto">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-900/60 text-emerald-300 border border-emerald-500/30 uppercase tracking-wider font-mono">
                      BACK • DETAILED PROOF & ANSWER
                    </span>
                    <span className="text-xs text-slate-400">Click to flip back</span>
                  </div>

                  {/* Detailed Answer */}
                  <div className="text-sm md:text-base text-slate-200 leading-relaxed whitespace-pre-line mb-4">
                    {currentCard.answer}
                  </div>

                  {/* Formula Render */}
                  {currentCard.formula && (
                    <div className="p-4 bg-dark-bg/90 rounded-xl border border-slate-800 my-4">
                      <div className="text-xs font-semibold text-cyan-400 mb-2">Key Formula:</div>
                      <div className="text-center overflow-x-auto py-1">
                        <MathView math={currentCard.formula} block={true} className="text-brand-300 text-lg" />
                      </div>
                    </div>
                  )}

                  {/* Paper Figure Asset */}
                  {currentCard.figureUrl && (
                    <div className="my-4 p-4 bg-dark-bg/90 rounded-xl border border-slate-800 text-center space-y-2">
                      <img 
                        src={currentCard.figureUrl} 
                        alt={currentCard.figureCaption || 'Paper Figure'} 
                        className="max-h-52 mx-auto rounded-lg border border-slate-700 shadow-lg object-contain cursor-zoom-in"
                        onClick={(e) => { e.stopPropagation(); setZoomFigureUrl(currentCard.figureUrl!); }}
                      />
                      {currentCard.figureCaption && (
                        <p className="text-xs text-slate-400 italic">{currentCard.figureCaption}</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t border-slate-800 text-xs text-slate-400 flex items-center justify-between">
                  <span>Concept ID: <code className="text-brand-300 font-mono">{currentCard.conceptId}</code></span>
                  <span className="text-slate-400">Rate your recall below</span>
                </div>
              </div>
            </div>
          </div>

          {/* Spaced Repetition Rating Controls */}
          <div className="flex flex-wrap items-center justify-center gap-3 p-4 bg-dark-card/60 rounded-2xl border border-slate-800">
            <button
              onClick={() => handleRating(0)}
              className="flex-1 min-w-[100px] py-2.5 px-4 bg-rose-950/80 hover:bg-rose-900 border border-rose-500/40 text-rose-200 rounded-xl text-xs font-bold transition-all shadow-sm text-center"
            >
              🔴 Again (1 min)
            </button>
            <button
              onClick={() => handleRating(1)}
              className="flex-1 min-w-[100px] py-2.5 px-4 bg-amber-950/80 hover:bg-amber-900 border border-amber-500/40 text-amber-200 rounded-xl text-xs font-bold transition-all shadow-sm text-center"
            >
              🟠 Hard (1 day)
            </button>
            <button
              onClick={() => handleRating(2)}
              className="flex-1 min-w-[100px] py-2.5 px-4 bg-blue-950/80 hover:bg-blue-900 border border-blue-500/40 text-blue-200 rounded-xl text-xs font-bold transition-all shadow-sm text-center"
            >
              🔵 Good (3 days)
            </button>
            <button
              onClick={() => handleRating(3)}
              className="flex-1 min-w-[100px] py-2.5 px-4 bg-emerald-950/80 hover:bg-emerald-900 border border-emerald-500/40 text-emerald-200 rounded-xl text-xs font-bold transition-all shadow-sm text-center"
            >
              🟢 Easy (7 days)
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 text-slate-400">
          No flashcards found for selected filter tag.
        </div>
      )}

      {/* Zoom Figure Modal */}
      {zoomFigureUrl && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md p-6 flex items-center justify-center animate-fade-in">
          <div className="relative max-w-4xl w-full bg-dark-card border border-slate-700 p-6 rounded-2xl shadow-2xl">
            <button
              onClick={() => setZoomFigureUrl(null)}
              className="absolute top-4 right-4 bg-slate-800 hover:bg-slate-700 text-white p-2 rounded-xl text-xs font-bold"
            >
              ✕ Close
            </button>
            <h4 className="text-lg font-bold text-white mb-4">Original Paper Asset Figure</h4>
            <img src={zoomFigureUrl} alt="Zoomed Figure" className="max-h-[70vh] mx-auto rounded-xl border border-slate-800" />
          </div>
        </div>
      )}
    </div>
  );
};
